/* eslint-disable prefer-destructuring */
import queryString from 'qs';
import { get, map, mapValues, omit, range, isArray } from 'lodash';
import { replace } from 'react-router-redux';
import moment from 'moment';

import config from '../config';
import filterMapping from '../config/filterOptions-new';
import filterConfigurationType from '../config/filters/shared';

import tvtApi from '../services/tvt-api';
import algoliaApi from '../services/algolia-api';
import {
  addAsyncLabels,
  addSampleLabel,
  addDateLabels,
  addSeasonsLabels,
  serializeFilters,
  parseFilters,
  formatLabel
} from '../utils/filters';
import { getSegments } from './audience-segment-actions';
import { createEpisodeFilterOptionsLoader } from '../services/loaders';
import { convertDateRangeToDaysInPast } from '../utils/dates';

export const FILTER_CLEARED = 'FILTER_CLEARED';
export const FILTERS_CLEARED = 'FILTERS_CLEARED';
export const FILTERS_UPDATED = 'FILTERS_UPDATED';
export const FILTERS_RESET = 'FILTERS_RESET';
export const FILTERS_RESET_ALL = 'FILTERS_RESET_ALL';
export const LOAD_FILTERS_SUCCESS = 'LOAD_FILTERS_SUCCESS';
export const GET_FILTERS_CONFIG_REQUEST = 'GET_FILTERS_CONFIG_REQUEST';
export const GET_FILTERS_CONFIG_SUCCESS = 'GET_FILTERS_CONFIG_SUCCESS';
export const GET_FILTERS_CONFIG_ERROR = 'GET_FILTERS_CONFIG_ERROR';
export const UPDATE_FILTER_OPTIONS = 'UPDATE_FILTER_OPTIONS';
export const SHOW_SEASONS_SUCCESS = 'SHOW_SEASONS_SUCCESS';

export const updateFilterOptions = (filter, options) => ({
  type: UPDATE_FILTER_OPTIONS,
  filter,
  options
});

export const loadShowSeasons = seasonCount => {
  const seasons = map(range(1, seasonCount + 1), s => ({
    value: `${s}`,
    label: `Season ${s}`
  }));
  return {
    type: SHOW_SEASONS_SUCCESS,
    seasons
  };
};

export const loadAvailableFilters = filters => ({
  type: LOAD_FILTERS_SUCCESS,
  filters
});

const mapFilter = (filter, id, booleanSeparator) => {
  let filterValue;
  if (filter) {
    const mapFilterValue = ({ aliasValue, value, label }) => ({
      value: aliasValue || value,
      label
    });
    filterValue = isArray(filter)
      ? filter.map(mapFilterValue)
      : mapFilterValue(filter);
  }
  return {
    id,
    value: filterValue,
    booleanSeparator
  };
};

const syncFiltersWithUrl = (filters, booleanFilters = {}) => (
  dispatch,
  getState
) => {
  const prevLocation = get(getState(), 'router.location', {});
  const fs = mapValues(filters, (filter, id) => {
    const booleanSeparator = get(booleanFilters, id, 'OR');
    return mapFilter(filter, id, booleanSeparator);
  }); // dreadful, but needed

  const filtersQuery = serializeFilters(fs);
  const nextSearch = {};
  const selectedBenchmark = get(filters, 'benchmark', null);
  const selectedSegment = get(filters, ['segments'], null);

  if (selectedBenchmark) {
    nextSearch.benchmark = selectedBenchmark;
  }

  if (selectedSegment) {
    nextSearch.segment = selectedSegment;
  } else {
    nextSearch.filters = filtersQuery;
  }

  const nextLocation = {
    ...prevLocation,
    search: queryString.stringify(nextSearch)
  };

  dispatch(replace(nextLocation));
};

export const updateFilters = (
  filters,
  options = { syncWithUrl: true },
  booleanFilters
) => dispatch => {
  if (options.syncWithUrl) {
    dispatch(syncFiltersWithUrl(filters, booleanFilters));
  }

  dispatch({
    type: FILTERS_UPDATED,
    filters,
    booleanFilters
  });
};

const getFiltersFromUrl = (state, options) => {
  if (!options.sync) {
    return {};
  }
  const query = get(state, 'router.location.search', '').slice(1); // get rid of the initial ?
  const segmentQuery = get(queryString.parse(query), 'segment', null);
  if (segmentQuery) {
    return { segment: segmentQuery };
  }

  const filtersConfig = get(state, 'filters.config', {});
  const filtersQuery = get(queryString.parse(query), 'filters', []);
  const benchmarkQuery = get(queryString.parse(query), 'benchmark', null);
  const parsedFilters = parseFilters(filtersQuery);
  const withSampleLabel = addSampleLabel(parsedFilters);
  const withSeasons = addSeasonsLabels(withSampleLabel);
  const withMultiValues = mapValues(withSeasons, (f, id) =>
    get(filtersConfig, `${id}.isMulti`, false) ? f : get(f, '0', null)
  );

  const finalFilters = addDateLabels(withMultiValues);

  return {
    ...finalFilters,
    benchmark: benchmarkQuery,
    segment: segmentQuery
  };
};

const getFiltersFromUrlAsync = async (state, options) => {
  if (!options.sync) {
    return {};
  }

  const query = get(state, 'router.location.search', '').slice(1); // get rid of the initial ?
  const segmentQuery = get(queryString.parse(query), 'segment', null);

  if (segmentQuery) {
    return { segment: segmentQuery };
  }
  const filtersConfig = get(state, 'filters.config', {});
  const filtersQuery = get(queryString.parse(query), 'filters', []);
  const benchmarkQuery = get(queryString.parse(query), 'benchmark', null);

  const parsedFilters = parseFilters(filtersQuery);
  const withSampleLabel = addSampleLabel(parsedFilters);
  const withSeasons = addSeasonsLabels(withSampleLabel);
  if (withSeasons && withSeasons.studio && filtersConfig.studio.options) {
    withSeasons.studio = withSeasons.studio.map(s =>
      filtersConfig.studio.options.find(o => o.value === s.value)
    );
  }

  const withAsync = await addAsyncLabels(withSeasons);
  const withMultiValues = mapValues(withAsync, (f, id) =>
    get(filtersConfig, `${id}.isMulti`, false) ? f : get(f, '0', null)
  );
  const finalFilters = addDateLabels(withMultiValues);
  return {
    ...finalFilters,
    boolean: parsedFilters.boolean,
    benchmark: benchmarkQuery,
    segment: segmentQuery
  };
};

const formatDateGating = (daysFromStart, daysFromEnd) => {
  const start = moment()
    .subtract(daysFromStart, 'd')
    .format('YYYY/MM/DD');

  const end = moment()
    .subtract(daysFromEnd, 'd')
    .format('YYYY/MM/DD');

  return `${start} to ${end}`;
};

export const clearFilter = id => (dispatch, getState) => {
  const prevFilters = get(getState(), 'filters.fields', {});
  const nextFilters = mapValues(omit(prevFilters, id), 'value');
  const nextBooleanFilters = mapValues(
    omit(prevFilters, id),
    'booleanSeparator'
  );

  dispatch(syncFiltersWithUrl(nextFilters, nextBooleanFilters));

  dispatch({
    type: FILTER_CLEARED,
    id
  });
};

export const requestFiltersConfig = () => ({
  type: GET_FILTERS_CONFIG_REQUEST
});

export const loadFiltersConfig = (fetchedConfig = {}) => ({
  type: GET_FILTERS_CONFIG_SUCCESS,
  config: mapValues(fetchedConfig, (value, key) =>
    value.isAsync
      ? {
          ...value,
          loadOptions: filterMapping[key]
        }
      : value
  )
});

export const errorFiltersConfig = error => ({
  type: GET_FILTERS_CONFIG_ERROR,
  error
});

export const getFiltersConfig = () => async (dispatch, getState) => {
  dispatch(requestFiltersConfig());
  const existingFiltersConfig = get(getState(), 'filters.config');
  const state = getState();
  const { allowedDataCategories } = state.user;

  try {
    if (existingFiltersConfig) {
      allowedDataCategories.forEach(category => {
        const [id, access] = category.split(':');
        const hasExistingValue =
          get(existingFiltersConfig, [id, 'value'], null) ||
          get(existingFiltersConfig, [id, 'value', 'length'], null);
        if (access !== '*' && !hasExistingValue) {
          if (id === 'date') {
            const daysFromEnd = 0;
            const daysFromStart = 30 * access;
            const value = formatDateGating(daysFromStart, daysFromEnd);

            existingFiltersConfig[id].value = {
              label: value,
              value
            };
          } else {
            const values = access.split(',').map(v => ({ label: v, value: v }));
            existingFiltersConfig[id].value = values;
          }
        }
      });

      const configuration = {
        ...existingFiltersConfig,
        segments: filterConfigurationType.segments
      };

      await dispatch(loadFiltersConfig(existingFiltersConfig));
      await dispatch(getSegments(configuration));
    } else {
      const filtersConfig = await tvtApi.getFiltersConfig();

      allowedDataCategories.forEach(category => {
        const [id, access] = category.split(':');
        const hasExistingValue =
          get(filtersConfig, [id, 'value'], null) ||
          get(filtersConfig, [id, 'value', 'length'], null);
        if (access !== '*' && !hasExistingValue) {
          if (id === 'date') {
            const daysFromEnd = 0;
            const daysFromStart = 30 * access;
            const value = formatDateGating(daysFromStart, daysFromEnd);

            filtersConfig[id].value = {
              label: value,
              value
            };
          } else {
            const value = access.split(',').map(v => {
              const label = formatLabel(id, v);
              return { label, value: v };
            });
            filtersConfig[id].value = value;
          }
        }
      });

      const configuration = {
        ...filtersConfig,
        segments: filterConfigurationType.segments
      };

      await dispatch(loadFiltersConfig(configuration));
      await dispatch(getSegments(configuration));
    }
  } catch (err) {
    dispatch(errorFiltersConfig(err));
  }
};

export const clearFilters = () => ({ type: FILTERS_CLEARED });

export const clearTabbedFilters = (fields, omittedFields) => ({
  type: FILTERS_CLEARED,
  fields,
  omittedFields
});

const mergeConfigAndUrlFilters = async (
  getState,
  page,
  urlFilters,
  dispatch,
  options
) => {
  const filtersConfig = get(getState(), 'filters.config', {});
  const groupings = get(config.filters, `${page}.pageFilters`, []);
  let values = {};
  if (urlFilters.segment) {
    const segment = await tvtApi.getSegment(urlFilters.segment);
    const segmentFilters = JSON.parse(segment.filters);
    segmentFilters.forEach(f => {
      values[f.id] = f.value;
    });
  } else {
    values = urlFilters;
  }
  const pageFilters = map(groupings, group => {
    const groupFilters = mapValues(group.filters, (localFiltersConfig, id) => {
      const apiFilterConfig = get(filtersConfig, id, {});
      const localFilterValue = get(localFiltersConfig, 'value', null);
      const apiFilterValue = get(apiFilterConfig, 'value', null);
      const urlFilterValue = get(values, `${id}`, null);

      const localBooleanSeparatorValue = get(
        localFiltersConfig,
        'boolean',
        null
      );
      const urlBooleanSeparatorValue = get(urlFilters, `boolean.${id}`, null);
      const apiBooleanSeparatorValue = get(
        apiFilterConfig,
        'booleanSeparator',
        'OR'
      );

      if (id === 'episode') {
        apiFilterConfig.loadOptions = createEpisodeFilterOptionsLoader({
          showId: options.showId,
          seasons: urlFilters.season
        });
      }
      const tooltip = get(localFiltersConfig, 'tooltip', null);
      let isAliasMode = false;
      if (id === 'date') {
        isAliasMode = get(localFiltersConfig, 'isAliasMode', false);
        if (isAliasMode && urlFilterValue) {
          const daysInPastValue = convertDateRangeToDaysInPast(
            urlFilterValue.value
          );
          urlFilterValue.aliasValue = urlFilterValue.value;
          urlFilterValue.value = daysInPastValue.value;
        }
      }
      let isValidDatesMode = true;
      if (id === 'future_release_date') {
        isValidDatesMode = get(localFiltersConfig, 'isValidDatesMode', true);
      }
      return {
        ...apiFilterConfig,
        isAliasMode,
        isValidDatesMode,
        // localFilterValue must be applied last to make it possible to override a default value if it couldn't be applied
        // e.g. say we have a page which can show different content types ('Movies', 'Shows' or 'All' (both)), default value
        // for the content type filter in the configuration is set to `All`, next we login with a gated account for
        // which only 'Shows' is visible so in this case apiFilterValue must winn and default 'All' value from the configuration
        // should be overridden with 'Shows'. See ActorSummaryReport for example.
        value: urlFilterValue || apiFilterValue || localFilterValue,
        booleanSeparator:
          urlBooleanSeparatorValue ||
          localBooleanSeparatorValue ||
          apiBooleanSeparatorValue,
        tooltip,
        id
      };
    });

    if (
      page === 'reportBuilder' &&
      groupFilters.metric &&
      groupFilters.metric.options
    ) {
      const filterMetrics = m =>
        m.value !== 'viewshare' &&
        m.value !== 'affinity_index' &&
        m.value !== 'audience_overlap' &&
        m.value !== 'completion';

      groupFilters.metric.options = groupFilters.metric.options.filter(
        filterMetrics
      );
    } else if (
      (page === 'affinityReport' ||
        page === 'affinityReportMovies' ||
        page === 'affinityReportAll') &&
      groupFilters.metric
    ) {
      const filterMetrics = m =>
        m.value === 'affinity_index' || m.value === 'audience_overlap';

      if (groupFilters.metric.options) {
        groupFilters.metric.options = groupFilters.metric.options.filter(
          filterMetrics
        );
      }
    } else if (page === 'movieSummaryReport' && groupFilters.metric) {
      const filterMetrics = m =>
        m.value === 'engagement' ||
        m.value === 'followers' ||
        m.value === 'views' ||
        m.value === 'viewers' ||
        m.value === 'rating';

      if (groupFilters.metric.options) {
        groupFilters.metric.options = groupFilters.metric.options
          .filter(filterMetrics)
          .map(m =>
            m.value === 'engagement'
              ? { value: 'engagement', label: 'Engagement' }
              : m
          );
      }
    }

    return {
      ...group,
      filters: groupFilters
    };
  });

  dispatch(loadAvailableFilters(pageFilters));

  if (options.syncWithUrl) {
    const filtersForUrl = { ...get(getState(), 'filters.fields', {}) };
    const filterValuesForUrl = mapValues(filtersForUrl, 'value');
    const filterBooleanValuesForUrl = mapValues(
      filtersForUrl,
      'booleanSeparator'
    );

    if (urlFilters.benchmark) {
      filterValuesForUrl.benchmark = urlFilters.benchmark;
    }

    if (urlFilters.segment) {
      dispatch(syncFiltersWithUrl({ segments: urlFilters.segment }));
    } else {
      dispatch(
        syncFiltersWithUrl(filterValuesForUrl, filterBooleanValuesForUrl)
      );
    }
  }
};

export const getFiltersForPage = (page, options = { syncWithUrl: true }) => (
  dispatch,
  getState
) => {
  const urlFilters = getFiltersFromUrl(getState(), {
    sync: options.syncWithUrl
  });

  mergeConfigAndUrlFilters(getState, page, urlFilters, dispatch, options);
};

export const getFiltersForPageAsync = (
  page,
  options = { syncWithUrl: true, showId: null, movieId: null }
) => async (dispatch, getState) => {
  const urlFilters = await getFiltersFromUrlAsync(getState(), {
    sync: options.syncWithUrl
  });

  if (
    page === 'showReportAnticipation' ||
    page === 'showReportViewership' ||
    page === 'showReportEngagement'
  ) {
    urlFilters.show = { label: '', value: options.showId };
    const { options: shows } = await algoliaApi.getShowsById([options.showId]);

    if (shows.length > 0) {
      const label = get(shows, [0, 'label'], '');
      urlFilters.show.label = label;
    }
  } else if (
    page === 'movieReportAnticipation' ||
    page === 'movieReportViewership' ||
    page === 'movieReportEngagement'
  ) {
    urlFilters.movie = { label: '', value: options.movieId };
    const { options: movies } = await algoliaApi.getMoviesById([
      options.movieId
    ]);

    if (movies.length > 0) {
      const label = get(movies, [0, 'label'], '');
      urlFilters.movie.label = label;
    }
  } else if (page === 'attributionPlatform') {
    //urlFilters.platform = { label: '', value: options.platformId };
    //const { options: platforms } = await algoliaApi.getPlatformsById([
    //  options.platformId
    //]);
    //if (platforms.length > 0) {
    //  const label = get(platforms, [0, 'label'], '');
    //  urlFilters.platform.label = label;
    //}
  }

  return mergeConfigAndUrlFilters(
    getState,
    page,
    urlFilters,
    dispatch,
    options
  );
};

export const resetFilters = () => ({ type: FILTERS_RESET });
export const resetFiltersAll = () => ({ type: FILTERS_RESET_ALL });
